import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Spinner } from "@fluentui/react-components";
import {
    telephonyService,
    type CallLogDto,
    type DialTarget,
    type MyExtensionDto,
    type VoicemailItem,
} from "../../services/telephonyService";
import { useTelephony } from "../../context/TelephonyContext";
import { useAuth } from "../../context/AuthContext";
import { useWindowManager } from "../windowManager/WindowManagerContext";
import { openComposeWindow } from "../mail/ComposeMailWindow";
import { openVorgangFromCall } from "./callVorgangFlow";
import { I } from "../redesign/kvpIcons";
import CallDetailPanel from "./CallDetailPanel";
import AssignNumberDialog from "./AssignNumberDialog";
import CallVorgangButton from "./CallVorgangButton";
import { anzahlZuordnungen } from "./callZuordnungen";
import CallVorgangZuweisenDialog from "./CallVorgangZuweisenDialog";
import Dialpad from "./Dialpad";
import { callFollowUpTemplate } from "./callFollowUpMail";
import { createRueckrufVorgangFromCall } from "./callRueckrufVorgang";
import {
    ArrowInIcon,
    ArrowOutIcon,
    PersonAddIcon,
    PersonIcon,
    VoicemailIcon,
    RefreshIcon,
    PlayIcon,
    isMissedInbound,
    unterzeileTeile,
    fmtDuration,
    fmtDayMonth,
    fmtTime,
    anzeigeInitialen,
    initialsOf,
    counterpartyNumber,
    displayNameOf,
    normalizeDialNumber,
} from "./telephonyUtils";
import { weitereKontakteHinweis } from "./callKontakte";
import { ANRUF_NOTIZ_GESPEICHERT, notizKurz, type AnrufNotizGespeichert } from "./anrufNotiz";
import {
    bereichsOptionen,
    effektiveSuche,
    ladeOffset,
    neuLadenLimit,
    seiteAnhaengen,
    sichtbareAnrufe,
    siehtWieNummerAus,
    verlaufQuery,
    VERLAUF_SEITE,
    type VerlaufTab,
} from "./telephonyVerlauf";
import "../redesign/contactRedesign.css";
import { bestaetigen } from "../redesign/bestaetigen";

/**
 * TelephonyPage — neue In-App-Telefonzentrale unter `/anrufe` (SP3).
 *
 * Ersetzt die alte `Anrufe.tsx` (Timeline) UND `Voicemails.tsx` (Mailbox-Seite).
 * Zweispaltig im Refined/kvp-rd-Design:
 *  - LINKS: Platzhalter-Panel fürs Dialpad (kommt in SP5).
 *  - RECHTS: Master-Detail mit Tabs (Verlauf / Verpasst / Mailbox), Suche,
 *    Datums-Chips, tag-gruppierter Liste und einem Anruf-Detail-Panel.
 *
 * Verlauf und Verpasst laden seitenweise beim Scrollen nach (Infinite Scroll);
 * Suche, Zeitraum, Bereich und der Verpasst-Filter wirken serverseitig über die
 * ganze Historie. Die Tab-Zähler sind exakte Server-Zahlen (`total` einer
 * Abfrage mit `limit: 1`). Bei einer Suche stehen über der Anrufliste die
 * passenden Kontakte aus dem ganzen Bestand — mit Anrufen und Öffnen — und,
 * wenn der Begriff eine Nummer ist, „<Nummer> anrufen".
 */

// Format-/Ableitungs-Helfer und die Inline-Icons liegen in `telephonyUtils`
// (geteilt mit `CallDetailPanel`, kein Duplikat).

// ── Datums-Chips → from/to (from inkl., to exkl.; ISO) ───────────────────────
type DateChip = "alle" | "heute" | "gestern" | "woche" | "monat";
const DATE_CHIPS: { key: DateChip; label: string }[] = [
    { key: "alle", label: "Alle" },
    { key: "heute", label: "Heute" },
    { key: "gestern", label: "Gestern" },
    { key: "woche", label: "Letzte Woche" },
    { key: "monat", label: "Diesen Monat" },
];

function startOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function rangeFor(chip: DateChip): { from?: string; to?: string } {
    const now = new Date();
    const today = startOfDay(now);
    switch (chip) {
        case "heute":
            return { from: today.toISOString() };
        case "gestern": {
            const y = new Date(today);
            y.setDate(y.getDate() - 1);
            return { from: y.toISOString(), to: today.toISOString() };
        }
        case "woche": {
            const w = new Date(today);
            w.setDate(w.getDate() - 6); // letzte 7 Tage inkl. heute
            return { from: w.toISOString() };
        }
        case "monat": {
            const m = new Date(now.getFullYear(), now.getMonth(), 1);
            return { from: m.toISOString() };
        }
        default:
            return {};
    }
}

// ── Tag-Gruppierung der Liste ────────────────────────────────────────────────
type DayGroupKey = "heute" | "gestern" | "woche" | "aelter";
const DAY_GROUP_ORDER: DayGroupKey[] = ["heute", "gestern", "woche", "aelter"];
const DAY_GROUP_LABEL: Record<DayGroupKey, string> = {
    heute: "Heute",
    gestern: "Gestern",
    woche: "Letzte Woche",
    aelter: "Älter",
};

function dayGroupOf(iso: string, now: Date): DayGroupKey {
    const start = startOfDay(now).getTime();
    const day = startOfDay(new Date(iso)).getTime();
    const diff = Math.round((start - day) / 86400000);
    if (diff <= 0) return "heute";
    if (diff === 1) return "gestern";
    if (diff <= 7) return "woche";
    return "aelter";
}

type Tab = "verlauf" | "verpasst" | "mailbox";

// Pause nach dem letzten Tastendruck, bevor die Suche an den Server geht.
const SUCHE_DEBOUNCE_MS = 300;
// So viele Kontakte holt der Kontakte-Block einer Suche höchstens.
const KONTAKTE_LIMIT = 30;
// So viele davon stehen sofort da; der Rest hinter „Alle zeigen".
const KONTAKTE_SICHTBAR = 4;

// Mobil (A4): Auf-/Zu-Zustand der Suche-&-Filter-Leiste, pro Session gemerkt
// (sessionStorage) — Default eingeklappt. Desktop ignoriert den Zustand (die
// Toolbar ist dort per CSS immer sichtbar, der Toggle ausgeblendet).
const MOBILE_FILTERS_KEY = "kvp.tp.mobileFiltersOpen";

// Unter dieser PANEL-Breite (nicht Viewport!) schaltet die Seite in den
// Kompaktmodus: keine Dialpad-Spalte (Tastenfeld-Button + Sheet), Kopf-Aktionen
// in eigener Zeile, Sichtbarkeitsbereich in der Filterleiste, Zeilen-Aktionen
// unter dem Namen.
const NARROW_MAX_PX = 1000;

/**
 * Misst die EIGENE Breite der Telefon-Seite (nicht den Viewport). Nötig, weil links
 * die ein-/ausklappbare Navigation steht: auf dem iPad quer bleiben neben der Sidebar
 * nur ~900px übrig — eine Viewport-Media-Query (1194px) würde dort fälschlich das
 * Desktop-Layout zeigen und die Liste zerquetschen.
 */
function useNarrowPanel<T extends HTMLElement>(): [React.RefObject<T>, boolean] {
    const ref = useRef<T>(null);
    const [narrow, setNarrow] = useState(false);
    // useLayoutEffect: erste Messung VOR dem Paint → kein Aufblitzen des
    // Desktop-Layouts auf schmalen Geräten.
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el) return;
        const apply = (w: number) => setNarrow(w > 0 && w < NARROW_MAX_PX);
        apply(el.clientWidth);
        const ro = new ResizeObserver((entries) => {
            const w = entries[0]?.contentRect.width;
            if (w != null) apply(w);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    return [ref, narrow];
}

export const TelephonyPage: React.FC = () => {
    const navigate = useNavigate();
    const [params, setParams] = useSearchParams();
    const { callNumber } = useTelephony();
    const { openWindow } = useWindowManager();

    // Default-Tab „Verpasst" (A3): ohne (bzw. mit unbekanntem) ?tab= öffnet die
    // Seite im Verpasst-Tab; Deep-Links ?tab=verlauf / ?tab=mailbox bleiben.
    const initialTab = ((): Tab => {
        const t = params.get("tab");
        return t === "mailbox" || t === "verlauf" ? t : "verpasst";
    })();
    const [tab, setTab] = useState<Tab>(initialTab);
    const [dateChip, setDateChip] = useState<DateChip>("alle");
    const [search, setSearch] = useState("");

    // Sichtbarkeitsbereich. Serverseitig gefiltert (telephony/calls ?scope=).
    //  - "Alle" (ganzer Mandant) NUR für admin.
    //  - "Team" nur, wenn der User mindestens ein Team hat — nicht für gfgl (T-37).
    //  - "Persönlich" für alle (bei service = nur eigene Anrufe, bei gfgl ohne
    //    FO/BO-Kunden; das Backend regelt das).
    // Default-Scope = „Persönlich" für ALLE Rollen (A2); einzige Ausnahme admin →
    // „Alle". Siehe bereichsOptionen.
    const { teams, isAdmin, isGfgl, user } = useAuth();
    // SP8: „ich" für den Auto-Rückruf-Vorgang (hauptverantwortlich = [ich]).
    const currentUserId: string | null = user?.id ?? null;
    // „Alle" wählt nur der Admin. Die Geschäftsführung sieht in dieser Liste
    // ihre eigenen Anrufe (samt der an sie weitergeleiteten) — in der
    // Kundenakte dagegen weiterhin alle Gespräche mit dem Kunden.
    const canSeeAll = isAdmin;
    // T-37: Die Geschäftsführung bekommt keine Team-Sicht in der Anrufliste.
    const hasTeam = (teams?.length ?? 0) > 0 && !isGfgl;
    const defaultScope = (): "all" | "own" | "team" => (isAdmin ? "all" : "own");
    const [scope, setScope] = useState<"all" | "own" | "team">(defaultScope);
    const [bulkBusy, setBulkBusy] = useState(false);
    const [bulkHinweis, setBulkHinweis] = useState<string | null>(null);
    // Scope gültig halten, wenn sich Rolle/Teams (nach-)laden oder ändern.
    useEffect(() => {
        const allowed: Array<"all" | "own" | "team"> = [];
        if (canSeeAll) allowed.push("all");
        if (hasTeam) allowed.push("team");
        allowed.push("own");
        if (!allowed.includes(scope)) setScope(defaultScope());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canSeeAll, hasTeam, scope]);
    const scopeOptions = useMemo(
        () => bereichsOptionen({ isAdmin: canSeeAll, isGfgl, hasTeam }),
        [canSeeAll, isGfgl, hasTeam],
    );
    // Kompaktmodus (Panel < 1000px, also Handy UND Tablet): die Dialpad-Spalte ist
    // ausgeblendet → ein-/ausklappbares Sheet als Zugang zum Tastenfeld (SP7).
    const [pageRef, narrow] = useNarrowPanel<HTMLDivElement>();
    const [mobileDialpadOpen, setMobileDialpadOpen] = useState(false);
    // Mobil (< 900px, A4): Suchfeld + Datums-Chips + Neu-laden hängen hinter
    // einem „Suche & Filter"-Toggle — standardmäßig eingeklappt, Zustand pro
    // Session. Desktop bleibt unverändert (Toggle dort per CSS ausgeblendet).
    const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(() => {
        try {
            return sessionStorage.getItem(MOBILE_FILTERS_KEY) === "1";
        } catch {
            return false;
        }
    });
    const toggleMobileFilters = useCallback(() => {
        setMobileFiltersOpen((prev) => {
            const next = !prev;
            try {
                sessionStorage.setItem(MOBILE_FILTERS_KEY, next ? "1" : "0");
            } catch {
                /* sessionStorage voll/blockiert — Merken ist best-effort */
            }
            return next;
        });
    }, []);

    const [me, setMe] = useState<MyExtensionDto | null>(null);
    const [todayCount, setTodayCount] = useState<number>(0);

    const [calls, setCalls] = useState<CallLogDto[]>([]);
    // Server-`total` der aktuellen Liste (Zeitraum, Bereich, Tab-Filter und
    // Suche) — entscheidet, ob beim Scrollen noch etwas nachzuladen ist.
    const [listTotal, setListTotal] = useState(0);
    const [callsLoading, setCallsLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [callsError, setCallsError] = useState<string | null>(null);
    const [moreError, setMoreError] = useState<string | null>(null);
    // Exakte Tab-Zähler (Zeitraum + Bereich, OHNE Suche) vom Server.
    const [counts, setCounts] = useState<{ verlauf: number; verpasst: number }>({
        verlauf: 0,
        verpasst: 0,
    });

    const [voicemails, setVoicemails] = useState<VoicemailItem[]>([]);
    const [vmLoading, setVmLoading] = useState(true);
    const [vmError, setVmError] = useState<string | null>(null);

    const [selectedId, setSelectedId] = useState<number | null>(null);

    // Deep-Link ?call=<id> (z. B. aus dem Mitteilungszentrum): Detail dieses
    // Anrufs öffnen und den Parameter wieder entfernen — auch wenn die Seite
    // schon offen ist. Ist der Anruf nicht in der geladenen Liste, bleibt die
    // Liste stehen.
    const callParam = params.get("call");
    useEffect(() => {
        if (!callParam) return;
        const id = Number(callParam);
        if (Number.isInteger(id) && id > 0) setSelectedId(id);
        const p = new URLSearchParams(params);
        p.delete("call");
        setParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callParam]);

    // Suche: sofort im Feld, mit kurzer Pause an den Server. Unter zwei
    // Zeichen gilt sie als leer (`effektiveSuche`).
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedSearch(search), SUCHE_DEBOUNCE_MS);
        return () => window.clearTimeout(t);
    }, [search]);
    const serverSuche = effektiveSuche(debouncedSearch);
    const listTab: VerlaufTab = tab === "verpasst" ? "verpasst" : "verlauf";

    // Tab-Wechsel spiegelt sich in der URL (?tab=…), damit /anrufe/mailbox per
    // Redirect direkt im Mailbox-Tab landet und Deep-Links funktionieren.
    // „Verpasst" ist der Default (A3) → ohne Parameter; Verlauf/Mailbox explizit.
    const switchTab = useCallback(
        (next: Tab) => {
            setTab(next);
            setSelectedId(null);
            const p = new URLSearchParams(params);
            if (next === "verpasst") p.delete("tab");
            else p.set("tab", next);
            setParams(p, { replace: true });
        },
        [params, setParams],
    );

    // ── Datenladen ──
    const loadMeAndToday = useCallback(async () => {
        const [meRes] = await Promise.all([
            telephonyService.myExtension().catch(() => null),
        ]);
        setMe(meRes);
        telephonyService
            // Steht unter „VoIP-Leitung: <Nebenstelle>" und meint die eigenen
            // Anrufe des Tages, nicht die des Hauses.
            .listCalls({ ...rangeFor("heute"), limit: 1, scope: "own" })
            .then((r) => setTodayCount(r.total))
            .catch(() => setTodayCount(0));
    }, []);

    // Jede neue Abfrage (Tab, Zeitraum, Bereich, Suche) bekommt eine Nummer.
    // Antworten älterer Abfragen — etwa einer Suche, die noch unterwegs war,
    // als schon weitergetippt wurde — werden verworfen, laufende abgebrochen.
    const reqSeq = useRef(0);
    const abortRef = useRef<AbortController | null>(null);
    const loadingMoreRef = useRef(false);
    // Aktuelle Liste für Callbacks, die nicht bei jeder Änderung neu entstehen sollen.
    const callsRef = useRef(calls);
    callsRef.current = calls;
    const selectedIdRef = useRef(selectedId);
    selectedIdRef.current = selectedId;
    // Scrollposition der Liste — bleibt über den Wechsel ins Detail und zurück
    // und über ein Neuladen an Ort und Stelle erhalten.
    const listScrollTop = useRef(0);

    /**
     * Die Liste von vorn laden. `anOrtUndStelle`: so viele Zeilen holen wie
     * schon geladen, ohne Lade-Spinner — nach einer Kontakt-Zuordnung sollen
     * die Namen erscheinen, ohne dass die Liste zurück nach oben springt.
     */
    const loadCalls = useCallback(
        async (opts: { anOrtUndStelle?: boolean } = {}) => {
            const seq = ++reqSeq.current;
            abortRef.current?.abort();
            const ctrl = new AbortController();
            abortRef.current = ctrl;
            loadingMoreRef.current = false;
            setLoadingMore(false);
            setMoreError(null);
            if (!opts.anOrtUndStelle) {
                setCallsLoading(true);
                listScrollTop.current = 0;
            }
            setCallsError(null);
            try {
                const res = await telephonyService.listCalls(
                    {
                        ...verlaufQuery({
                            tab: listTab,
                            range: rangeFor(dateChip),
                            scope,
                            suche: serverSuche,
                            offset: 0,
                            limit: opts.anOrtUndStelle
                                ? neuLadenLimit(callsRef.current.length)
                                : VERLAUF_SEITE,
                        }),
                        // Alle Vorgänge je Anruf (Zahl am Knopf, Detail).
                        mitZuordnungen: true,
                    },
                    { signal: ctrl.signal },
                );
                if (seq !== reqSeq.current) return;
                // Das offene Detail nicht unter den Füßen wegziehen: Passt der
                // gewählte Anruf nicht mehr zum Filter (etwa im Verpasst-Tab
                // gerade abgehakt), bleibt er beim Neuladen an Ort und Stelle
                // erhalten — in der Liste steht er dann ohnehin nicht mehr.
                const offen = opts.anOrtUndStelle
                    ? callsRef.current.find((c) => c.id === selectedIdRef.current)
                    : undefined;
                setCalls(
                    offen && !res.calls.some((c) => c.id === offen.id)
                        ? [...res.calls, offen]
                        : res.calls,
                );
                setListTotal(res.total);
            } catch (e) {
                if (seq !== reqSeq.current || ctrl.signal.aborted) return;
                setCallsError((e as Error).message);
            } finally {
                if (seq === reqSeq.current) setCallsLoading(false);
            }
        },
        [listTab, dateChip, scope, serverSuche],
    );

    // Die nächste Seite anhängen (Infinite Scroll). Ab welcher Zeile, sagt
    // `ladeOffset` — im Verpasst-Tab zählen gerade abgehakte Anrufe nicht mit.
    const loadMoreCalls = useCallback(async () => {
        if (loadingMoreRef.current) return;
        const current = callsRef.current;
        const offset = ladeOffset(current, listTab);
        if (offset >= listTotal) return;
        const seq = reqSeq.current;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        setMoreError(null);
        try {
            const res = await telephonyService.listCalls(
                {
                    ...verlaufQuery({
                        tab: listTab,
                        range: rangeFor(dateChip),
                        scope,
                        suche: serverSuche,
                        offset,
                        limit: VERLAUF_SEITE,
                    }),
                    mitZuordnungen: true,
                },
                { signal: abortRef.current?.signal },
            );
            if (seq !== reqSeq.current) return;
            setCalls((prev) => seiteAnhaengen(prev, res.calls));
            // Ohne neue Zeilen ist das Ende erreicht, auch wenn sich `total`
            // inzwischen verschoben hat — sonst lüde der Beobachter endlos.
            setListTotal(res.calls.length === 0 ? offset : res.total);
        } catch (e) {
            if (seq !== reqSeq.current) return;
            setMoreError((e as Error).message);
        } finally {
            if (seq === reqSeq.current) {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            }
        }
    }, [listTab, dateChip, scope, serverSuche, listTotal]);

    // Tab-Zähler: exakte Server-Zahlen für Zeitraum + Bereich (ohne Suche).
    const countSeq = useRef(0);
    const loadCounts = useCallback(async () => {
        const seq = ++countSeq.current;
        const range = rangeFor(dateChip);
        try {
            const [alle, verpasst] = await Promise.all([
                telephonyService.listCalls({ ...range, scope, limit: 1 }),
                telephonyService.listCalls({ ...range, scope, limit: 1, missedOnly: true }),
            ]);
            if (seq !== countSeq.current) return;
            setCounts({ verlauf: alle.total, verpasst: verpasst.total });
        } catch {
            /* Zähler sind Beiwerk — die Liste zeigt ihren eigenen Fehler */
        }
    }, [dateChip, scope]);

    const loadVoicemails = useCallback(async () => {
        setVmLoading(true);
        setVmError(null);
        try {
            const res = await telephonyService.listVoicemails(1, 100);
            setVoicemails(res.voicemails);
        } catch (e) {
            setVmError((e as Error).message);
        } finally {
            setVmLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadMeAndToday();
        void loadVoicemails();
    }, [loadMeAndToday, loadVoicemails]);

    // Neue Suche, neuer Tab, neuer Zeitraum oder Bereich → Liste von vorn.
    useEffect(() => {
        void loadCalls();
    }, [loadCalls]);

    useEffect(() => {
        void loadCounts();
    }, [loadCounts]);

    // Laufende Abfrage beim Verlassen der Seite abbrechen.
    useEffect(() => () => abortRef.current?.abort(), []);

    // ── abgeleitete Werte ──
    const now = useMemo(() => new Date(), [calls]); // stabil pro Calls-Load
    const vmUnread = useMemo(() => voicemails.filter((v) => !v.isRead).length, [voicemails]);
    const hasMore = ladeOffset(calls, listTab) < listTotal;

    // Verpasst-Tab zeigt NUR offene Verpasste (`isOpenMissed`, identisch zum
    // Serverfilter `missedOnly` und zum Badge): Ein gerade abgehakter Anruf
    // bleibt in `calls` (das offene Detail braucht ihn), fällt aber aus der Liste.
    const filteredCalls = useMemo(() => sichtbareAnrufe(calls, listTab), [calls, listTab]);

    const grouped = useMemo(() => {
        const map = new Map<DayGroupKey, CallLogDto[]>();
        for (const c of filteredCalls) {
            const k = dayGroupOf(c.startedAt, now);
            const arr = map.get(k);
            if (arr) arr.push(c);
            else map.set(k, [c]);
        }
        return DAY_GROUP_ORDER.filter((k) => map.has(k)).map((k) => ({
            key: k,
            label: DAY_GROUP_LABEL[k],
            items: map.get(k)!,
        }));
    }, [filteredCalls, now]);

    const filteredVoicemails = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return voicemails;
        return voicemails.filter((v) => {
            const name = (v.fromName ?? "").toLowerCase();
            const num = (v.fromNumber ?? "").toLowerCase();
            return name.includes(q) || num.includes(q);
        });
    }, [voicemails, search]);

    const selectedCall = useMemo(
        () => (selectedId == null ? null : calls.find((c) => c.id === selectedId) ?? null),
        [selectedId, calls],
    );

    const onCallback = useCallback(
        (c: CallLogDto) => {
            const num = counterpartyNumber(c);
            if (num) callNumber(num, c.contactDisplayName ?? undefined);
        },
        [callNumber],
    );

    // Eine Notiz aus dem Gespräch ist angehängt (nach dem Auflegen) — in der
    // schon geladenen Zeile nachziehen, ohne die Liste neu zu laden.
    useEffect(() => {
        const aufNotiz = (e: Event) => {
            const d = (e as CustomEvent<AnrufNotizGespeichert>).detail;
            if (!d) return;
            setCalls((prev) => prev.map((c) => (c.id === d.callLogId ? { ...c, notes: d.notes } : c)));
        };
        window.addEventListener(ANRUF_NOTIZ_GESPEICHERT, aufNotiz);
        return () => window.removeEventListener(ANRUF_NOTIZ_GESPEICHERT, aufNotiz);
    }, []);

    const onCallsChanged = useCallback(
        (patched: CallLogDto) => {
            setCalls((prev) => prev.map((c) => (c.id === patched.id ? patched : c)));
            // T-192: Hängt eine Mailbox-Nachricht an diesem Anruf, zeigt ihr
            // Vorgang-Knopf die neue Zahl der Zuordnungen.
            setVoicemails((prev) =>
                prev.map((v) =>
                    v.callLogId === patched.id ? { ...v, anzahlZuordnungen: anzahlZuordnungen(patched) } : v,
                ),
            );
            // Erledigt-Stand kann sich geändert haben → Verpasst-Zähler nachziehen.
            void loadCounts();
        },
        [loadCounts],
    );

    /**
     * Nach einer Kontakt-Zuordnung oder -Anlage: die Liste an Ort und Stelle
     * neu vom Server holen. Das Backend hat dabei auch die übrigen Anrufe
     * derselben Nummer dem Kontakt zugeschlagen — die sollen ihren Namen
     * ebenfalls zeigen, nicht nur die eine Zeile, von der aus zugeordnet wurde.
     */
    const onKontaktZugeordnet = useCallback(() => {
        void loadCalls({ anOrtUndStelle: true });
        void loadCounts();
    }, [loadCalls, loadCounts]);

    // Erledigt-Häkchen direkt aus der Zeile (A5/A6): optimistisch umschalten, dann
    // PATCH /telephony/calls/:id { resolved }. Die Server-Antwort (inkl. aufgelöstem
    // resolvedByUserName) ersetzt danach den optimistischen Wert; bei Fehler wird der
    // ursprüngliche Anruf zurückgerollt.
    const onToggleResolved = useCallback(async (c: CallLogDto) => {
        const wantResolved = !c.resolvedAt;
        setCalls((prev) =>
            prev.map((x) =>
                x.id === c.id
                    ? { ...x, resolvedAt: wantResolved ? new Date().toISOString() : null }
                    : x,
            ),
        );
        try {
            const updated = await telephonyService.updateCall(c.id, { resolved: wantResolved });
            setCalls((prev) => prev.map((x) => (x.id === c.id ? updated : x)));
            void loadCounts();
        } catch {
            // Zurückrollen auf den Stand vor dem Klick.
            setCalls((prev) => prev.map((x) => (x.id === c.id ? c : x)));
        }
    }, [loadCounts]);

    // A9/SP4: Mail-/Vorgang-Aktion auf einem Anruf OHNE Kontakt öffnet zuerst den
    // Nummer-Zuweisen-Dialog; nach der Zuordnung läuft die Aktion mit dem Kontakt
    // weiter. `assignFor` merkt sich Anruf + die anschließende Aktion.
    // `number` ohne `call`: „Kontakt mit dieser Nummer anlegen" aus der Suche.
    const [assignFor, setAssignFor] = useState<
        | { call: CallLogDto; purpose: "mail" | "vorgang" | "kontakt" }
        | { call?: undefined; number: string; purpose: "kontakt" }
        | null
    >(null);
    // Menüpunkt „Bestehendem Vorgang zuweisen": braucht KEINEN Kontakt (der
    // gewählte Vorgang hat seinen Kunden bereits) und geht deshalb direkt auf.
    const [vorgangZuweisenFor, setVorgangZuweisenFor] = useState<CallLogDto | null>(null);

    // Die eigentliche Aktion, sobald der Anruf einen Kontakt hat.
    // Mail (SP5/A10): Compose vorbefüllt mit der Follow-up-Vorlage nach Richtung
    // (eingehend verpasst → (b), sonst nicht erreicht → (a)) und der Standard-
    // E-Mail des Kontakts als Empfänger. Ohne Kontakt-Mail bleibt das
    // Empfänger-Feld leer (Hinweis: Adresse muss ergänzt werden). Der Versand
    // passiert bewusst im Compose-Fenster (kein Auto-Send).
    const runMail = useCallback(
        (c: CallLogDto) => {
            const tpl = callFollowUpTemplate(c.direction);
            openComposeWindow(openWindow, {
                to: c.contactEmail ? [c.contactEmail] : [],
                subject: tpl.subject,
                // T-41: Die Büroklammer startet mit den Dokumenten des Anrufkontakts.
                contactId: c.contactId ?? null,
                // A2: Firma → „Hallo zusammen,"; Person → nur mit sicherem Vornamen,
                // sonst keine automatische Anrede (kein Gruß aus dem Anzeigenamen).
                body: tpl.body({ isCompany: c.contactIsCompany ?? undefined, firstName: c.contactFirstName }),
                // SP8 (A11/A12): Nach ERFOLGREICHEM Versand automatisch einen
                // „Rückruf <Kontakt>"-Vorgang anlegen und den Anruf verknüpfen +
                // erledigen. Nur bei bekanntem Kontakt (sonst kein KV/kein Titel);
                // bei kontaktlosen Anrufen läuft zuvor der Zuweisen-Dialog (SP4).
                onSent: c.contactId
                    ? async () => {
                          const updated = await createRueckrufVorgangFromCall(c, currentUserId);
                          onCallsChanged(updated);
                      }
                    : undefined,
            });
        },
        [openWindow, currentUserId, onCallsChanged],
    );
    // Vorgang (SP7/A13): zweistufiger Erstell-Flow mit Kontakt-Vorbelegung,
    // Betreff „Anruf vom <Datum>" + Erst-Notiz. Nach dem Anlegen wird der Anruf
    // an den Vorgang gebunden und automatisch erledigt (Entscheidung 59);
    // `onCallsChanged` zieht die Liste sofort nach.
    const runVorgang = useCallback(
        (c: CallLogDto) => {
            openVorgangFromCall(openWindow, c, onCallsChanged);
        },
        [openWindow, onCallsChanged],
    );

    const onMailCompose = useCallback(
        (c: CallLogDto) => {
            if (c.contactId) runMail(c);
            else setAssignFor({ call: c, purpose: "mail" });
        },
        [runMail],
    );
    const onNewVorgang = useCallback(
        (c: CallLogDto) => {
            if (c.contactId) runVorgang(c);
            else setAssignFor({ call: c, purpose: "vorgang" });
        },
        [runVorgang],
    );
    const onVorgangZuweisen = useCallback((c: CallLogDto) => setVorgangZuweisenFor(c), []);
    /**
     * T-192: Vorgang-Knopf einer Mailbox-Nachricht. Die Nachricht selbst ist
     * nur ein Abzug aus der Anlage; zugeordnet wird der Anruf, der in ihr
     * endete — mit denselben Wegen wie aus der Anrufliste. Den vollständigen
     * Anruf erst beim Klick holen (nicht für jede Nachricht der Liste).
     */
    const onVoicemailVorgang = useCallback(
        async (vm: VoicemailItem, art: "neu" | "zuweisen") => {
            if (!vm.callId || vm.callLogId == null) return;
            const res = await telephonyService.listCalls({ callId: vm.callId, limit: 1, mitZuordnungen: true });
            const anruf = res.calls.find((c) => c.id === vm.callLogId);
            if (!anruf) throw new Error("Der Anruf zu dieser Nachricht ist für dich nicht sichtbar.");
            if (art === "neu") onNewVorgang(anruf);
            else onVorgangZuweisen(anruf);
        },
        [onNewVorgang, onVorgangZuweisen],
    );
    // „Kontakt" in der Zeile: mit Kontakt → Akte öffnen; ohne → zuordnen/anlegen.
    const onKontakt = useCallback(
        (c: CallLogDto) => {
            if (c.contactId) navigate(`/contacts/${c.contactId}`);
            else setAssignFor({ call: c, purpose: "kontakt" });
        },
        [navigate],
    );

    /**
     * Nach erfolgreicher Zuordnung: Liste neu vom Server (Kontaktname erscheint
     * auch bei den übrigen Anrufen derselben Nummer) und die ursprünglich
     * gewählte Aktion mit dem zugeordneten Anruf fortsetzen.
     *
     * Die Aktion kommt als Argument, nicht aus `assignFor`: Bei der vollen
     * Kontaktanlage ist der Dialog längst zu (und `assignFor` leer), wenn der
     * Kontakt gespeichert wird.
     */
    const onNumberAssigned = useCallback(
        (purpose: "mail" | "vorgang" | "kontakt", updated: CallLogDto | null) => {
            setAssignFor(null);
            onKontaktZugeordnet();
            if (!updated) return;
            onCallsChanged(updated);
            if (purpose === "mail") runMail(updated);
            else if (purpose === "vorgang") runVorgang(updated);
        },
        [onKontaktZugeordnet, onCallsChanged, runMail, runVorgang],
    );

    const onMarkVmRead = useCallback(async (msgId: string) => {
        await telephonyService.markVoicemailRead(msgId);
        setVoicemails((prev) => prev.map((v) => (v.msgId === msgId ? { ...v, isRead: true } : v)));
    }, []);

    const onDeleteVm = useCallback(async (msgId: string) => {
        if (
            !(await bestaetigen({
                titel: "Voicemail löschen?",
                text: "Die Sprachnachricht lässt sich danach nicht mehr abhören.",
                ja: "Löschen",
                ton: "danger",
            }))
        )
            return;
        await telephonyService.deleteVoicemail(msgId);
        setVoicemails((prev) => prev.filter((v) => v.msgId !== msgId));
    }, []);

    /**
     * Verpasste Anrufe stapelweise abhaken. Gedacht für den Altbestand: Nach dem
     * Reparatur-Import stehen dort Anrufe aus vielen Monaten, die längst erledigt
     * sind. Die Grenze ist bewusst einstellbar — was in den letzten Tagen
     * verpasst wurde, will niemand pauschal wegräumen.
     */
    const onBulkResolve = useCallback(
        async (tageZurueck: number | null) => {
            const before =
                tageZurueck === null
                    ? undefined
                    : new Date(Date.now() - tageZurueck * 86400_000).toISOString();
            const frage =
                tageZurueck === null
                    ? "Alle offenen verpassten Anrufe in dieser Ansicht als erledigt markieren?"
                    : `Alle verpassten Anrufe, die älter als ${tageZurueck} Tage sind, als erledigt markieren?`;
            if (
                !(await bestaetigen({
                    titel: "Als erledigt markieren?",
                    text: `${frage}\n\nSie bleiben im Verlauf sichtbar.`,
                    ja: "Als erledigt markieren",
                }))
            )
                return;
            setBulkBusy(true);
            try {
                const res = await telephonyService.resolveMissedBulk({ before, scope });
                setBulkHinweis(
                    res.erledigt === 0
                        ? "Es gab nichts zu erledigen."
                        : `${res.erledigt} verpasste Anrufe als erledigt markiert.`,
                );
                void loadCalls();
                void loadCounts();
                void loadMeAndToday();
            } catch (e) {
                setBulkHinweis((e as Error).message);
            } finally {
                setBulkBusy(false);
            }
        },
        [scope, loadCalls, loadCounts, loadMeAndToday],
    );

    const refreshActive = useCallback(() => {
        if (tab === "mailbox") void loadVoicemails();
        else {
            void loadCalls();
            void loadCounts();
            void loadMeAndToday();
        }
    }, [tab, loadVoicemails, loadCalls, loadCounts, loadMeAndToday]);

    // Sichtbarkeitsbereich (Alle / Team / Persönlich). EIN Renderort: breit im Kopf,
    // im Kompaktmodus in der Filterleiste (dort gehört er fachlich hin — er filtert
    // die Liste wie Suche und Zeitraum). Nie beides gleichzeitig.
    // Nur eine Option (z. B. Geschäftsführung, T-37) → kein einzelner Knopf.
    const scopeSegment = scopeOptions.length < 2 ? null : (
        <div className="rbac-seg" role="group" aria-label="Sichtbarkeitsbereich">
            {scopeOptions.map((o) => (
                <button
                    key={o.value}
                    type="button"
                    className={"rbac-seg-btn" + (scope === o.value ? " on" : "")}
                    aria-pressed={scope === o.value}
                    onClick={() => setScope(o.value)}
                >
                    <span className="rbac-dot" />
                    {o.label}
                </button>
            ))}
        </div>
    );

    return (
        <div className={`kvp-rd tp-page${narrow ? " tp-narrow" : ""}`} ref={pageRef}>
            {/* Kopfzeile: VoIP-Leitung + Mailbox / Neuer Kontakt */}
            <header className="tp-header">
                <div className="tp-line">
                    <span className="tp-line-ico">{I.phone}</span>
                    <div>
                        <div className="tp-line-title">
                            {me?.extension
                                ? `VoIP-Leitung: ${me.extension}`
                                : "Keine VoIP-Leitung zugewiesen"}
                        </div>
                        <div className="tp-line-sub">
                            {todayCount} {todayCount === 1 ? "Anruf" : "Anrufe"} heute
                        </div>
                    </div>
                </div>
                <div className="tp-header-actions">
                    {/* Mobil-Zugang zum Tastenfeld — nur < 900px sichtbar (die
                        Dialpad-Spalte ist dort ausgeblendet). */}
                    <button
                        type="button"
                        className="tp-hbtn tp-dialpad-toggle"
                        onClick={() => setMobileDialpadOpen(true)}
                        aria-label="Tastenfeld öffnen"
                    >
                        {I.phone}
                        Tastenfeld
                    </button>
                    {/* Sichtbarkeitsbereich — nur breit im Kopf; im Kompaktmodus steht
                        er in der Filterleiste (siehe unten). */}
                    {!narrow && scopeSegment}
                    <button
                        type="button"
                        className="tp-hbtn"
                        onClick={() => switchTab("mailbox")}
                        aria-label="Mailbox öffnen"
                    >
                        {VoicemailIcon}
                        Mailbox
                        {vmUnread > 0 && <span className="tp-hbadge">{vmUnread}</span>}
                    </button>
                    <button
                        type="button"
                        className="tp-hbtn primary"
                        onClick={() => navigate("/contacts/new/private")}
                    >
                        {I.plus}
                        Neuer Kontakt
                    </button>
                </div>
            </header>

            <div className="tp-cols">
                {/* LINKS: Dialpad-Panel (SP5) */}
                <aside className="tp-dialpad-col" aria-label="Tastenfeld">
                    <Dialpad />
                </aside>

                {/* RECHTS: Master-Detail */}
                <section className="tp-main">
                    {selectedCall ? (
                        <CallDetailPanel
                            key={selectedCall.id}
                            call={selectedCall}
                            onBack={() => setSelectedId(null)}
                            onCallback={onCallback}
                            onChanged={onCallsChanged}
                            onKontaktZugeordnet={onKontaktZugeordnet}
                        />
                    ) : (
                        <>
                            <nav className="tp-tabs" aria-label="Anruf-Ansichten">
                                <TabButton
                                    on={tab === "verlauf"}
                                    label="Verlauf"
                                    badge={counts.verlauf}
                                    onClick={() => switchTab("verlauf")}
                                />
                                <TabButton
                                    on={tab === "verpasst"}
                                    label="Verpasst"
                                    // Exakt vom Server (missedOnly) — kein Mindestwert mehr.
                                    badge={counts.verpasst}
                                    alert={counts.verpasst > 0}
                                    onClick={() => switchTab("verpasst")}
                                />
                                <TabButton
                                    on={tab === "mailbox"}
                                    label="Mailbox"
                                    badge={vmUnread}
                                    alert={vmUnread > 0}
                                    onClick={() => switchTab("mailbox")}
                                />
                            </nav>

                            {/* Mobil (< 900px, A4): Toggle für die Suche-&-Filter-
                                Leiste — auf dem Desktop per CSS ausgeblendet. Der
                                Punkt signalisiert aktive Suche/Filter im
                                eingeklappten Zustand. */}
                            <button
                                type="button"
                                className={`tp-filters-toggle${mobileFiltersOpen ? " open" : ""}`}
                                onClick={toggleMobileFilters}
                                aria-expanded={mobileFiltersOpen}
                                aria-controls="tp-toolbar"
                            >
                                {I.search}
                                Suche &amp; Filter
                                {(search.trim() !== "" ||
                                    (tab !== "mailbox" &&
                                        (dateChip !== "alle" || scope !== defaultScope()))) && (
                                    <span className="tp-filters-dot" aria-hidden />
                                )}
                                <span className="tp-filters-chev" aria-hidden>
                                    {I.chevD}
                                </span>
                            </button>

                            <div
                                id="tp-toolbar"
                                className={`tp-toolbar${mobileFiltersOpen ? " mobile-open" : ""}`}
                            >
                                <div className="search">
                                    {I.search}
                                    <input
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                        placeholder={
                                            tab === "mailbox"
                                                ? "Mailbox durchsuchen …"
                                                : "Name oder Nummer suchen …"
                                        }
                                        aria-label="Suche"
                                    />
                                </div>
                                {/* Kompaktmodus: Sichtbarkeitsbereich sitzt hier statt im
                                    Kopf (im Mailbox-Tab filtert er nichts → entfällt). */}
                                {narrow && tab !== "mailbox" && scopeSegment}
                                {tab !== "mailbox" && (
                                    <div className="tp-chips" role="group" aria-label="Zeitraum">
                                        {DATE_CHIPS.map((c) => (
                                            <button
                                                key={c.key}
                                                type="button"
                                                className={`tp-chip${dateChip === c.key ? " on" : ""}`}
                                                onClick={() => setDateChip(c.key)}
                                            >
                                                {c.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <button
                                    type="button"
                                    className="tp-iconbtn"
                                    onClick={refreshActive}
                                    aria-label="Neu laden"
                                    title="Neu laden"
                                    style={tab === "mailbox" ? { marginLeft: "auto" } : undefined}
                                >
                                    {RefreshIcon}
                                </button>
                            </div>

                            {/* Aufräumen im Verpasst-Tab: Der Reparatur-Import macht
                                Team-Anrufe aus vielen Monaten sichtbar — die gehören
                                ins Archiv, nicht in die Aufgabenliste. */}
                            {tab === "verpasst" && grouped.length > 0 && (
                                <div className="tp-bulk">
                                    <span className="tp-bulk-k">Aufräumen</span>
                                    <button
                                        type="button"
                                        className="tp-bulk-btn"
                                        disabled={bulkBusy}
                                        onClick={() => void onBulkResolve(30)}
                                    >
                                        Älter als 30 Tage
                                    </button>
                                    <button
                                        type="button"
                                        className="tp-bulk-btn"
                                        disabled={bulkBusy}
                                        onClick={() => void onBulkResolve(7)}
                                    >
                                        Älter als 7 Tage
                                    </button>
                                    <button
                                        type="button"
                                        className="tp-bulk-btn"
                                        disabled={bulkBusy}
                                        onClick={() => void onBulkResolve(null)}
                                    >
                                        Alle
                                    </button>
                                    {bulkHinweis && <span className="tp-bulk-hint">{bulkHinweis}</span>}
                                </div>
                            )}

                            {tab === "mailbox" ? (
                                <MailboxList
                                    voicemails={filteredVoicemails}
                                    loading={vmLoading}
                                    error={vmError}
                                    onMarkRead={onMarkVmRead}
                                    onDelete={onDeleteVm}
                                    onVorgang={onVoicemailVorgang}
                                />
                            ) : (
                                <CallList
                                    groups={grouped}
                                    loading={callsLoading}
                                    error={callsError}
                                    selectedId={selectedId}
                                    onSelect={setSelectedId}
                                    onCallback={onCallback}
                                    onToggleResolved={onToggleResolved}
                                    onMail={onMailCompose}
                                    onNewVorgang={onNewVorgang}
                                    onVorgangZuweisen={onVorgangZuweisen}
                                    onKontakt={onKontakt}
                                    hasMore={hasMore}
                                    loadingMore={loadingMore}
                                    moreError={moreError}
                                    onLoadMore={loadMoreCalls}
                                    scrollTopRef={listScrollTop}
                                    kopf={
                                        serverSuche ? (
                                            <SuchKopf
                                                suche={serverSuche}
                                                hatAnrufe={grouped.length > 0}
                                                onAnrufen={(nummer, name) => callNumber(nummer, name)}
                                                onKontaktOeffnen={(id) => navigate(`/contacts/${id}`)}
                                            />
                                        ) : null
                                    }
                                    leer={
                                        serverSuche ? (
                                            <SuchLeer
                                                suche={serverSuche}
                                                verpasst={tab === "verpasst"}
                                                onAnrufen={(nummer) => callNumber(nummer)}
                                                onKontaktAnlegen={(nummer) =>
                                                    setAssignFor({ number: nummer, purpose: "kontakt" })
                                                }
                                            />
                                        ) : (
                                            <span>
                                                {tab === "verpasst"
                                                    ? "Keine verpassten Anrufe im gewählten Zeitraum."
                                                    : "Keine Anrufe im gewählten Zeitraum."}
                                            </span>
                                        )
                                    }
                                />
                            )}
                        </>
                    )}
                </section>
            </div>

            {/* Mobil-Dialpad (< 900px): ein-/ausklappbares Bottom-Sheet. Auf dem
                Desktop nie sichtbar (Toggle ist per CSS ausgeblendet); der Scrim
                schließt bei Klick daneben, der Wählvorgang schließt via onAfterDial. */}
            {mobileDialpadOpen && (
                <div
                    className="tp-dp-sheet-scrim"
                    role="presentation"
                    onClick={() => setMobileDialpadOpen(false)}
                >
                    <div
                        className="tp-dp-sheet"
                        role="dialog"
                        aria-label="Tastenfeld"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tp-dp-sheet-head">
                            <span>Tastenfeld</span>
                            <button
                                type="button"
                                className="tp-iconbtn"
                                onClick={() => setMobileDialpadOpen(false)}
                                aria-label="Tastenfeld schließen"
                                title="Schließen"
                            >
                                {I.chevD}
                            </button>
                        </div>
                        <Dialpad onAfterDial={() => setMobileDialpadOpen(false)} />
                    </div>
                </div>
            )}

            {/* A9/SP4: Nummer-Zuweisen-Dialog für Anrufe ohne Kontakt (aus Mail-/
                Vorgang-Aktion). Nach der Zuordnung läuft die Aktion weiter. */}
            {assignFor && (
                <AssignNumberDialog
                    call={assignFor.call ?? null}
                    number={assignFor.call ? undefined : assignFor.number}
                    purpose={assignFor.purpose}
                    // „Kontakt mit dieser Nummer anlegen" → gleich die Anlage.
                    startModus={assignFor.call ? undefined : "create"}
                    onClose={() => setAssignFor(null)}
                    onAssigned={(updated) => onNumberAssigned(assignFor.purpose, updated)}
                />
            )}

            {/* Menüpunkt „Bestehendem Vorgang zuweisen". */}
            {vorgangZuweisenFor && (
                <CallVorgangZuweisenDialog
                    call={vorgangZuweisenFor}
                    onClose={() => setVorgangZuweisenFor(null)}
                    onAssigned={onCallsChanged}
                />
            )}
        </div>
    );
};

// ── Tab-Button ───────────────────────────────────────────────────────────────
const TabButton: React.FC<{
    on: boolean;
    label: string;
    badge: number;
    /** true → Badge ist ein Mindestwert (noch nicht alles geladen) → „+" anhängen. */
    approx?: boolean;
    alert?: boolean;
    onClick: () => void;
}> = ({ on, label, badge, approx, alert, onClick }) => (
    <button type="button" className={`tp-tab${on ? " on" : ""}`} onClick={onClick}>
        {label}
        <span className={`tp-tab-badge${alert ? " alert" : ""}`}>
            {badge}
            {approx && badge > 0 ? "+" : ""}
        </span>
    </button>
);

// ── Anrufliste (tag-gruppiert, lädt beim Scrollen nach) ─────────────────────
const CallList: React.FC<{
    groups: { key: string; label: string; items: CallLogDto[] }[];
    loading: boolean;
    error: string | null;
    selectedId: number | null;
    onSelect: (id: number) => void;
    onCallback: (c: CallLogDto) => void;
    onToggleResolved: (c: CallLogDto) => void;
    onMail: (c: CallLogDto) => void;
    onNewVorgang: (c: CallLogDto) => void;
    onVorgangZuweisen: (c: CallLogDto) => void;
    onKontakt: (c: CallLogDto) => void;
    hasMore: boolean;
    loadingMore: boolean;
    moreError: string | null;
    onLoadMore: () => void;
    /** Scrollposition, die den Wechsel ins Detail und zurück überlebt. */
    scrollTopRef: React.MutableRefObject<number>;
    /** Über der Liste (bei einer Suche: Kontakte / Nummer anrufen). */
    kopf?: React.ReactNode;
    /** Inhalt des Leer-Zustands. */
    leer: React.ReactNode;
}> = ({
    groups,
    loading,
    error,
    selectedId,
    onSelect,
    onCallback,
    onToggleResolved,
    onMail,
    onNewVorgang,
    onVorgangZuweisen,
    onKontakt,
    hasMore,
    loadingMore,
    moreError,
    onLoadMore,
    scrollTopRef,
    kopf,
    leer,
}) => {
    const wrapRef = useRef<HTMLDivElement>(null);
    const sentinelRef = useRef<HTMLDivElement>(null);
    const zeilen = groups.reduce((n, g) => n + g.items.length, 0);

    // Scrollposition wiederherstellen (zurück aus dem Detail, Neuladen an Ort
    // und Stelle) bzw. nach oben, wenn die Liste neu begonnen hat.
    useLayoutEffect(() => {
        const el = wrapRef.current;
        if (el && !loading) el.scrollTop = scrollTopRef.current;
    }, [loading, scrollTopRef]);

    // Infinite Scroll: Ein unsichtbarer Streifen am Listenende lädt die nächste
    // Seite, sobald er in Sichtweite kommt (300px Vorlauf). Der Beobachter wird
    // nach jeder Seite neu angelegt — ist die Liste noch zu kurz zum Scrollen,
    // meldet er sich sofort wieder und die nächste Seite folgt.
    useEffect(() => {
        const root = wrapRef.current;
        const target = sentinelRef.current;
        if (!root || !target || !hasMore || loadingMore || moreError) return;
        if (typeof IntersectionObserver === "undefined") return;
        const io = new IntersectionObserver(
            (entries) => {
                if (entries.some((e) => e.isIntersecting)) onLoadMore();
            },
            { root, rootMargin: "0px 0px 300px 0px" },
        );
        io.observe(target);
        return () => io.disconnect();
    }, [hasMore, loading, loadingMore, moreError, onLoadMore, zeilen]);

    if (loading) {
        return (
            <div className="tp-loading">
                <Spinner label="Lade Anrufe …" />
            </div>
        );
    }
    if (error) return <div className="tp-err">{error}</div>;
    return (
        <div
            className="tp-listwrap"
            ref={wrapRef}
            onScroll={(e) => {
                scrollTopRef.current = e.currentTarget.scrollTop;
            }}
        >
            {kopf}
            {groups.length === 0 ? (
                <div className="tp-empty">
                    <span className="tp-empty-ico">{ArrowInIcon}</span>
                    {leer}
                </div>
            ) : (
                groups.map((g) => (
                    <div key={g.key} className="tp-daygroup">
                        <div className="tp-dayhead">{g.label}</div>
                        {g.items.map((c) => (
                            <CallRow
                                key={c.id}
                                call={c}
                                selected={c.id === selectedId}
                                onSelect={() => onSelect(c.id)}
                                onCallback={() => onCallback(c)}
                                onResolve={() => onToggleResolved(c)}
                                onMail={() => onMail(c)}
                                onVorgang={() => onNewVorgang(c)}
                                onVorgangZuweisen={() => onVorgangZuweisen(c)}
                                onKontakt={() => onKontakt(c)}
                            />
                        ))}
                    </div>
                ))
            )}
            {hasMore && (
                <div className="tp-loadmore" ref={sentinelRef}>
                    {moreError ? (
                        <>
                            <span className="tp-muted" style={{ color: "var(--danger)" }}>
                                Weitere Anrufe konnten nicht geladen werden.
                            </span>
                            <button type="button" className="tp-detail-btn" onClick={onLoadMore}>
                                {RefreshIcon}
                                Erneut versuchen
                            </button>
                        </>
                    ) : (
                        <Spinner size="tiny" label="Weitere Anrufe werden geladen …" />
                    )}
                </div>
            )}
        </div>
    );
};

// ── Bei einer Suche: passende Kontakte aus dem ganzen Bestand ───────────────
/**
 * Steht bei einer Suche über der Anrufliste: alle passenden KMVS-Kontakte —
 * auch solche, mit denen es noch nie einen Anruf gab — samt ihren Nummern zum
 * Anrufen und einem Knopf zum Öffnen der Akte. Quelle ist die Schnellwahl-Suche
 * (`telephony/dial-search`, berechtigungsgeprüft, nur Kontakte mit Telefon-
 * oder Mobilnummer). Sieht der Begriff wie eine Nummer aus, steht obendrüber
 * zusätzlich „<Nummer> anrufen" — sofern die Liste Anrufe zeigt; sonst bietet
 * das der Leer-Zustand an.
 */
const SuchKopf: React.FC<{
    suche: string;
    hatAnrufe: boolean;
    onAnrufen: (nummer: string, name?: string) => void;
    onKontaktOeffnen: (contactId: number) => void;
}> = ({ suche, hatAnrufe, onAnrufen, onKontaktOeffnen }) => {
    const [treffer, setTreffer] = useState<DialTarget[]>([]);
    const [laedt, setLaedt] = useState(false);
    const [alle, setAlle] = useState(false);

    useEffect(() => {
        const ctrl = new AbortController();
        setLaedt(true);
        setAlle(false);
        telephonyService
            .dialSearch(suche, { limit: KONTAKTE_LIMIT, signal: ctrl.signal })
            .then((r) => {
                if (!ctrl.signal.aborted) setTreffer(r);
            })
            .catch(() => {
                if (!ctrl.signal.aborted) setTreffer([]);
            })
            .finally(() => {
                if (!ctrl.signal.aborted) setLaedt(false);
            });
        return () => ctrl.abort();
    }, [suche]);

    const nummer = siehtWieNummerAus(suche) ? normalizeDialNumber(suche) : "";
    const sichtbar = alle ? treffer : treffer.slice(0, KONTAKTE_SICHTBAR);
    if (!nummer && !laedt && treffer.length === 0) return null;

    return (
        <div className="tp-suchkopf" aria-label="Passende Kontakte">
            {nummer && hatAnrufe && (
                <button
                    type="button"
                    className="tp-detail-btn primary tp-such-anrufen"
                    onClick={() => onAnrufen(nummer)}
                >
                    {I.phone}
                    {suche} anrufen
                </button>
            )}
            {(laedt || treffer.length > 0) && (
                <div className="tp-suchkontakte">
                    <div className="tp-dayhead">
                        Kontakte
                        {!laedt && ` (${treffer.length}${treffer.length >= KONTAKTE_LIMIT ? "+" : ""})`}
                    </div>
                    {laedt && treffer.length === 0 ? (
                        <div className="tp-muted">Kontakte werden gesucht …</div>
                    ) : (
                        sichtbar.map((t) => (
                            <div key={t.contactId} className="tp-suchkontakt">
                                <span className="tp-avatar" aria-hidden>
                                    {initialsOf(t.displayName) ?? PersonIcon}
                                </span>
                                <div className="tp-rowmain">
                                    <div className="tp-rowname" title={t.displayName}>
                                        {t.displayName}
                                    </div>
                                    {/* T-12: Firma einer Person, damit gleichnamige
                                        Ansprechpartner unterscheidbar sind. */}
                                    {t.companyName && (
                                        <div className="tp-rowsub" title={t.companyName}>
                                            {t.companyName}
                                        </div>
                                    )}
                                    <div className="tp-suchnummern">
                                        {t.numbers.map((n) => (
                                            <button
                                                key={n.value}
                                                type="button"
                                                className="tp-suchnummer"
                                                onClick={() => onAnrufen(n.value, t.displayName)}
                                                title={`${n.value} anrufen`}
                                            >
                                                {I.phone}
                                                {n.value}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    className="tp-rowact"
                                    onClick={() => onKontaktOeffnen(t.contactId)}
                                    aria-label={`Kontakt öffnen: ${t.displayName}`}
                                    title="Kontakt öffnen"
                                >
                                    {PersonIcon}
                                </button>
                            </div>
                        ))
                    )}
                    {!alle && treffer.length > KONTAKTE_SICHTBAR && (
                        <button type="button" className="tp-such-mehr" onClick={() => setAlle(true)}>
                            Alle {treffer.length} Kontakte zeigen
                        </button>
                    )}
                    {alle && treffer.length >= KONTAKTE_LIMIT && (
                        <div className="tp-muted">
                            Es gibt weitere Treffer — für weniger Ergebnisse die Suche genauer eingeben.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ── Leer-Zustand einer Suche ────────────────────────────────────────────────
/** Keine Anrufe gefunden. Ist der Begriff eine Nummer: anrufen oder als Kontakt anlegen. */
const SuchLeer: React.FC<{
    suche: string;
    verpasst: boolean;
    onAnrufen: (nummer: string) => void;
    onKontaktAnlegen: (nummer: string) => void;
}> = ({ suche, verpasst, onAnrufen, onKontaktAnlegen }) => {
    const nummer = siehtWieNummerAus(suche) ? normalizeDialNumber(suche) : "";
    return (
        <>
            <span>
                {verpasst
                    ? `Keine offenen verpassten Anrufe zu „${suche}“.`
                    : `Keine Anrufe zu „${suche}“ im gewählten Zeitraum.`}
            </span>
            {nummer && (
                <div className="tp-leer-aktionen">
                    <button type="button" className="tp-detail-btn primary" onClick={() => onAnrufen(nummer)}>
                        {I.phone}
                        {suche} anrufen
                    </button>
                    <button type="button" className="tp-detail-btn" onClick={() => onKontaktAnlegen(suche)}>
                        {PersonAddIcon}
                        Kontakt mit dieser Nummer anlegen
                    </button>
                </div>
            )}
        </>
    );
};

// ── Eine Anruf-Zeile ─────────────────────────────────────────────────────────
const CallRow: React.FC<{
    call: CallLogDto;
    selected: boolean;
    onSelect: () => void;
    onCallback: () => void;
    onResolve: () => void;
    onMail: () => void;
    onVorgang: () => void;
    onVorgangZuweisen: () => void;
    onKontakt: () => void;
}> = ({
    call,
    selected,
    onSelect,
    onCallback,
    onResolve,
    onMail,
    onVorgang,
    onVorgangZuweisen,
    onKontakt,
}) => {
    const missed = isMissedInbound(call);
    const resolved = !!call.resolvedAt;
    // A6: erledigte verpasste Anrufe werden NICHT mehr rot markiert (schwarz/neutral).
    const showMissed = missed && !resolved;
    const name = displayNameOf(call);
    const num = counterpartyNumber(call);
    const initials = anzeigeInitialen(call);
    // Weitere Kontakte des Anrufs: „+2" hinter dem Hauptkontakt, Namen im Tooltip.
    const plus = weitereKontakteHinweis(call);

    const dirClass = missed
        ? resolved
            ? "missed resolved"
            : "missed"
        : call.direction === "outbound"
          ? "out"
          : "in";
    const dirIcon = call.direction === "outbound" ? ArrowOutIcon : ArrowInIcon;

    // Subzeile: Firma · Nummer (wenn ein Name angezeigt wird) · Status · Team.
    const subParts = unterzeileTeile(call);
    const sub = subParts.join(" · ");

    return (
        <div
            className={`tp-row${selected ? " sel" : ""}`}
            role="button"
            tabIndex={0}
            onClick={onSelect}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect();
                }
            }}
        >
            <span className={`tp-dir ${dirClass}`} aria-hidden>
                {dirIcon}
            </span>
            <span className="tp-avatar" aria-hidden>
                {initials ?? I.phone}
            </span>
            <div className="tp-rowmain">
                <div className={`tp-rowname${showMissed ? " missed" : ""}`} title={name}>
                    {name}
                    {plus && (
                        <span className="tp-rowplus" title={plus.title}>
                            {plus.text}
                        </span>
                    )}
                </div>
                {sub && (
                    <div className="tp-rowsub" title={sub}>
                        {sub}
                    </div>
                )}
                {call.notes?.trim() && (
                    <div className="tp-rownotiz" title={call.notes}>
                        {notizKurz(call.notes, 120)}
                    </div>
                )}
            </div>
            <div className="tp-rowmeta">
                {/* Datum ohne Jahr + Uhrzeit — in der Gruppe „Älter" ist die Uhrzeit
                    allein wertlos. Leere Teile fallen raus, damit ein unlesbares
                    Datum kein führendes Komma hinterlässt. */}
                <span className="tp-rowtime">
                    {[fmtDayMonth(call.startedAt), fmtTime(call.startedAt)]
                        .filter(Boolean)
                        .join(", ")}
                </span>
                {call.talkDurationSec != null && (
                    <span className="tp-rowdur">{fmtDuration(call.talkDurationSec)}</span>
                )}
            </div>
            {/* Aktionsleiste (A5/A7/A8): Zurückrufen → ✓ Erledigt → Mail → Neuer
                Vorgang → Kontakt. Alle Buttons stoppen die Propagation, damit die Zeile weiter
                das Detail öffnet. */}
            <div className="tp-rowactions">
                <button
                    type="button"
                    className="tp-rowcall"
                    disabled={!num}
                    onClick={(e) => {
                        e.stopPropagation();
                        onCallback();
                    }}
                    aria-label="Zurückrufen"
                    title={num ? `Zurückrufen: ${num}` : "Keine Nummer"}
                >
                    {I.phone}
                </button>
                <button
                    type="button"
                    className={`tp-rowact tp-rowresolve${resolved ? " done" : ""}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onResolve();
                    }}
                    aria-pressed={resolved}
                    aria-label={resolved ? "Als offen markieren" : "Als erledigt markieren"}
                    title={resolved ? "Erledigt — Klick öffnet wieder" : "Als erledigt markieren"}
                >
                    {I.check}
                </button>
                <button
                    type="button"
                    className="tp-rowact"
                    onClick={(e) => {
                        e.stopPropagation();
                        onMail();
                    }}
                    aria-label="E-Mail schreiben"
                    title="E-Mail schreiben"
                >
                    {I.mail}
                </button>
                <CallVorgangButton
                    variante="zeile"
                    onNeu={onVorgang}
                    onZuweisen={onVorgangZuweisen}
                    anzahl={anzahlZuordnungen(call)}
                />
                {/* Kontakt: mit Kontakt die Akte öffnen, ohne die Nummer
                    zuordnen oder einen Kontakt anlegen. Immer an derselben
                    Stelle, damit die Leiste nicht springt. */}
                <button
                    type="button"
                    className={`tp-rowact${call.contactId ? "" : " tp-rowkontakt-neu"}`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onKontakt();
                    }}
                    aria-label={call.contactId ? "Kontakt öffnen" : "Kontakt zuordnen oder anlegen"}
                    title={
                        call.contactId
                            ? `Kontakt öffnen: ${call.contactDisplayName ?? ""}`.trim()
                            : "Nummer einem Kontakt zuordnen oder neuen Kontakt anlegen"
                    }
                >
                    {call.contactId ? PersonIcon : PersonAddIcon}
                </button>
            </div>
        </div>
    );
};

// ── Mailbox-Liste (Logik aus Voicemails.tsx übernommen) ──────────────────────
const MailboxList: React.FC<{
    voicemails: VoicemailItem[];
    loading: boolean;
    error: string | null;
    onMarkRead: (msgId: string) => Promise<void>;
    onDelete: (msgId: string) => Promise<void>;
    onVorgang: (vm: VoicemailItem, art: "neu" | "zuweisen") => Promise<void>;
}> = ({ voicemails, loading, error, onMarkRead, onDelete, onVorgang }) => {
    if (loading) {
        return (
            <div className="tp-loading">
                <Spinner label="Lade Voicemails …" />
            </div>
        );
    }
    if (error) return <div className="tp-err">{error}</div>;
    if (voicemails.length === 0) {
        return (
            <div className="tp-empty">
                <span className="tp-empty-ico">{VoicemailIcon}</span>
                <span>Keine Voicemails in deiner Mailbox.</span>
            </div>
        );
    }
    return (
        <div className="tp-listwrap">
            <div className="tp-daygroup">
                {voicemails.map((vm) => (
                    <VoicemailRow
                        key={vm.msgId}
                        vm={vm}
                        onMarkRead={onMarkRead}
                        onDelete={onDelete}
                        onVorgang={onVorgang}
                    />
                ))}
            </div>
        </div>
    );
};

const VoicemailRow: React.FC<{
    vm: VoicemailItem;
    onMarkRead: (msgId: string) => Promise<void>;
    onDelete: (msgId: string) => Promise<void>;
    onVorgang: (vm: VoicemailItem, art: "neu" | "zuweisen") => Promise<void>;
}> = ({ vm, onMarkRead, onDelete, onVorgang }) => {
    const [audioUrl, setAudioUrl] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
        return () => {
            if (audioUrl) URL.revokeObjectURL(audioUrl);
        };
    }, [audioUrl]);

    const onPlay = useCallback(async () => {
        if (audioUrl) return;
        setBusy(true);
        setErr(null);
        try {
            const blob = await telephonyService.fetchVoicemailAudio(vm.msgId);
            setAudioUrl(URL.createObjectURL(blob));
            // Abspielen zählt als „gelesen".
            if (!vm.isRead) onMarkRead(vm.msgId).catch(() => undefined);
        } catch (e) {
            setErr((e as Error).message);
        } finally {
            setBusy(false);
        }
    }, [audioUrl, vm.msgId, vm.isRead, onMarkRead]);

    const vorgang = useCallback(
        (art: "neu" | "zuweisen") => {
            setErr(null);
            onVorgang(vm, art).catch((e) =>
                setErr((e as Error).message || "Der Anruf zu dieser Nachricht konnte nicht geladen werden."),
            );
        },
        [onVorgang, vm],
    );
    const ohneAnruf = vm.callLogId == null || !vm.callId;

    const name = vm.fromName || vm.fromNumber || "Unbekannter Anrufer";
    const initials = initialsOf(vm.fromName);
    const subParts: string[] = [];
    if (vm.fromName && vm.fromNumber) subParts.push(vm.fromNumber);
    subParts.push(fmtDuration(vm.durationSec));
    const sub = subParts.join(" · ");

    return (
        <div className={`tp-row tp-vm${vm.isRead ? "" : " unread"}`}>
            <div className="tp-vm-top">
                <span className="tp-dir in" aria-hidden>
                    {VoicemailIcon}
                </span>
                <span className="tp-avatar" aria-hidden>
                    {initials ?? VoicemailIcon}
                </span>
                <div className="tp-rowmain">
                    <div className="tp-rowname" title={name}>
                        {name}
                        {!vm.isRead && <span className="tp-vm-new" style={{ marginLeft: 8 }}>Neu</span>}
                    </div>
                    <div className="tp-rowsub" title={sub}>
                        {vm.time ? `${vm.time} · ` : ""}
                        {sub}
                    </div>
                </div>
                <div className="tp-vm-actions">
                    <button
                        type="button"
                        className="tp-playbtn"
                        onClick={onPlay}
                        disabled={busy || !!audioUrl}
                        aria-label="Abspielen"
                    >
                        {busy ? <Spinner size="tiny" /> : PlayIcon}
                        Anhören
                    </button>
                    <CallVorgangButton
                        variante="zeile"
                        onNeu={() => vorgang("neu")}
                        onZuweisen={() => vorgang("zuweisen")}
                        anzahl={vm.anzahlZuordnungen ?? 0}
                        gesperrtHinweis={
                            ohneAnruf
                                ? "Der Anruf zu dieser Nachricht ist noch nicht im Anrufverlauf angekommen – bitte in ein paar Minuten erneut versuchen."
                                : null
                        }
                    />
                    {!vm.isRead && (
                        <button
                            type="button"
                            className="tp-iconbtn"
                            onClick={() => onMarkRead(vm.msgId)}
                            aria-label="Als gelesen markieren"
                            title="Als gelesen markieren"
                        >
                            {I.check}
                        </button>
                    )}
                    <button
                        type="button"
                        className="tp-iconbtn danger"
                        onClick={() => onDelete(vm.msgId)}
                        aria-label="Löschen"
                        title="Löschen"
                    >
                        {I.trash}
                    </button>
                </div>
            </div>
            {err && (
                <span className="tp-muted" style={{ color: "var(--danger)", marginTop: 8 }}>
                    {err}
                </span>
            )}
            {audioUrl && (
                <div className="tp-vm-audio">
                    {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                    <audio className="tp-audio" controls autoPlay src={audioUrl} />
                </div>
            )}
        </div>
    );
};

export default TelephonyPage;
